import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAsync } from 'react-use'

import { Form as AntdForm, Card, Row, Col, Table, Modal, Input, Select, Button, Popconfirm, Tag, Space, Skeleton } from 'antd'
import { PlusOutlined, SearchOutlined, PrinterOutlined, EyeOutlined, DeleteOutlined, CheckOutlined, CloseOutlined } from '@ant-design/icons'

import dayjs from 'dayjs'
import { dbTodayjs } from '../../utils/date'

import { useAuth } from '../../contexts/AuthContextProvider'
import { useLayoutContext } from '../../contexts/LayoutContextProvider'
import { statusObj, methodObj } from '../../const/jpstudent'
import { genCsv } from '../../utils/csv'
import { search } from '../../utils/search'
import { formatNumber } from '../../utils/tools'
import { isAllowed } from '../../route/roles'

const List = () => {
  const navigate = useNavigate()
  const { db, role } = useAuth()
  const { getPageNo, onPageChange, getSearch, setSearch, getCoops, getCompanies } = useLayoutContext()

  const [updateCount, setUpdateCount] = useState(0)
  const [initValue, setInitValue] = useState([])
  const [value, setValue] = useState([])
  const [qsVisible, setQsVisible] = useState(false)
  const [qs, setQs] = useState({})
  const [tags, setTags] = useState([])

  const [coopObj, setCoopObj] = useState()
  const [companyObj, setCompanyObj] = useState()

  const [searchForm] = AntdForm.useForm()

  const { loading } = useAsync(async () => {
    const coops = await getCoops()
    const coopData = coops.reduce((acc, o) => {
      acc[o.id] = o
      return acc
    }, {})
    setCoopObj(coopData)

    const companies = await getCompanies()
    const companyData = companies.reduce((acc, o) => {
      acc[o.id] = o
      return acc
    }, {})
    setCompanyObj(companyData)

    const snap = await db.collection('/jpstudents').get()
    const data = snap.docs.map(doc => {
      const d = doc.data()
      return {
        ...d,
        id: doc.id,
        scn: d?.scn ?? '',
        traveldate: dbTodayjs(d?.traveldate),
        paymentdate: dbTodayjs(d?.paymentdate),
        startdate: dbTodayjs(d?.startdate),
        createddate: dbTodayjs(d?.createddate)
      }
    })

    const q = getSearch('jpstudents')
    setQs(q || {})
    setInitValue(data)
  }, [updateCount])

  useEffect(() => {
    const tagList = Object.keys(qs).reduce((acc, o) => {
      return (!qs[o]) ? acc : [...acc, {
        key: o,
        value: (o === 'companyid') ? companyObj[qs[o]]?.name : (o === 'coopid') ? coopObj[qs[o]]?.name : (o === 'status') ? statusObj[qs[o]]?.name : qs[o],
        label: {
          scn: 'รหัสนักเรียน',
          firstname: 'ชื่อ',
          lastname: 'นามสกุล',
          companyid: 'บริษัท',
          coopid: 'สหกรณ์',
          status: 'สถานะ'
        }[o]
      }]
    }, [])
    setTags(tagList)
    const data = search(initValue, qs)
    setValue(data)
  }, [initValue, qs, companyObj, coopObj])

  const handleClose = (key) => {
    const newQs = {
      ...qs,
      [key]: null
    }
    setQs(newQs)
    setSearch('jpstudents', newQs)
    searchForm.setFieldsValue(newQs)
  }

  const handleDelete = async (row) => {
    try {
      await db.collection('/jpstudents').doc(row.id).delete()
      setUpdateCount(updateCount + 1)
      return Promise.resolve()
    } catch (error) {
      return Promise.reject(error)
    }
  }

  const download = async (data) => {
    const list = data.map((o) => ({
      'รหัสนักเรียน': o?.scn ?? '',
      'ชื่อ': o?.firstname ?? '',
      'นามสกุล': o?.lastname ?? '',
      'บริษัท': companyObj[o?.companyid]?.name ?? '',
      'สหกรณ์': coopObj[o?.coopid]?.name ?? '',
      'สถานะ': statusObj[o?.status]?.name ?? '',
      'ประเภทการชำระเงิน': methodObj[o?.paymethod]?.name ?? '',
      'จำนวนเงิน': (o?.paymethod === 'loan' && o?.loan) ? o.loan : (o?.paymethod === 'cash' && o?.cashamount) ? o.cashamount : '',
      'วันที่ชำระ': (o?.paymethod === 'loan' && o?.startdate) ? dayjs(o.startdate).format('D MMM BBBB') : (o?.paymethod === 'cash' && o?.paymentdate) ? dayjs(o.paymentdate).format('D MMM BBBB') : '',
      'วันที่เดินทาง': (o?.traveldate) ? dayjs(o.traveldate).format('D MMM BBBB') : ''
    }))
    try {
      await genCsv('student_in_japan_list', list)
      return Promise.resolve()
    } catch (error) {
      return Promise.reject(error)
    }
  }

  const columns = [
    {
      title: 'รหัสนักเรียน',
      dataIndex: 'scn',
      width: 120
    },
    {
      title: 'ชื่อ',
      dataIndex: 'firstname',
      width: 150
    },
    {
      title: 'นามสกุล',
      dataIndex: 'lastname',
      width: 150
    },
    {
      title: 'บริษัท',
      dataIndex: 'companyid',
      width: 120,
      render: data => companyObj[data]?.name ?? ''
    },
    {
      title: 'สหกรณ์',
      dataIndex: 'coopid',
      width: 120,
      render: data => coopObj[data]?.name ?? ''
    },
    {
      title: 'สถานะ',
      dataIndex: 'status',
      width: 120,
      align: 'center',
      render: data => (
        <span style={{ color: statusObj[data]?.color ?? '' }}>{statusObj[data]?.name ?? ''}</span>
      )
    },
    {
      title: 'ประเภทการชำระเงิน',
      dataIndex: 'paymethod',
      width: 100,
      align: 'center',
      render: data => methodObj[data]?.name ?? ''
    },
    {
      title: 'จำนวนเงิน',
      dataIndex: 'loan',
      width: 100,
      align: 'center',
      render: (_, row) => (row?.paymethod === 'loan' && row?.loan) ? formatNumber(row.loan, 2) : (row?.paymethod === 'cash' && row?.cashamount) ? formatNumber(row.cashamount, 2) : ''
    },
    {
      title: 'วันที่ชำระ',
      dataIndex: 'startdate',
      width: 100,
      render: (_, row) => (row?.paymentdate) ? dayjs(row.paymentdate).format('D MMM BBBB') : ''
      // render: (_, row) => (row?.paymethod === 'loan' && row?.startdate) ? dayjs(row.startdate).format('D MMM BBBB') : (row?.paymethod === 'cash' && row?.paymentdate) ? dayjs(row.paymentdate).format('D MMM BBBB') : ''
    },
    {
      title: 'วันที่เดินทาง',
      dataIndex: 'traveldate',
      width: 120,
      render: data => (
        <span>{(data) ? dayjs(data).format('D MMM BBBB') : ''}</span>
      )
    },
    {
      title: 'เครื่องมือ',
      dataIndex: 'Operation',
      width: 175,
      render: (_, row) => (
        <div>
          <Button
            type='link'
            style={{ marginRight: '8px' }}
            icon={<EyeOutlined style={{ fontSize: 18 }} />}
            onClick={() => {
              const path = (isAllowed(role, 'edit', 'jpstudents')) ? `/admin/jpstudents/${row.id}/edit` : `/admin/jpstudents/${row.id}/view`
              navigate(path)
            }}
          ></Button>
          {(isAllowed(role, 'delete', 'jpstudents')) && (
            <Popconfirm
              title={`Are you sure to delete ${row.firstname} ${row.lastname} student?`}
              onConfirm={() => handleDelete(row)}
              okText='Yes'
              cancelText='No'
            >
              <Button
                type='link'
                style={{ marginRight: '8px' }}
                danger
                icon={<DeleteOutlined style={{ fontSize: 18 }} />}
              ></Button>
            </Popconfirm>
          )}
        </div>
      )
    }
  ]

  if (loading) return <Skeleton></Skeleton>
  return (
    <Card style={{ minHeight: '100%' }}>
      <Row>
        <Col flex='auto'>
          <div style={{ fontSize: 24 }}>
            รายชื่อนักเรียนไปญี่ปุ่น
          </div>
          <div style={{ marginTop: 12 }}>
            {tags.map((o) => (
              <Tag
                key={o.key}
                closable
                onClose={() => handleClose(o.key)}
              >
                {o.label}: {o.value}
              </Tag>
            ))}
          </div>
        </Col>
        <Col flex='300px'>
          <div style={{ float: 'right', fontSize: 14 }}>
            {(isAllowed(role, 'create', 'jpstudents')) && (
              <div>
                <Button
                  type='primary'
                  icon={<PlusOutlined style={{ fontSize: 14 }} />}
                  onClick={() => {
                    navigate('/admin/jpstudents/create')
                  }}
                >
                  เพิ่มนักเรียนไปญี่ปุ่น
                </Button>
              </div>
            )}
            <div style={{ float: 'right', margin: '8px 0 16px 0' }}>
              <Button
                icon={<SearchOutlined />}
                style={{ margin: '0 8px' }}
                onClick={() => {
                  setQsVisible(true)
                }}
              ></Button>
              <Button
                icon={<PrinterOutlined />}
                onClick={() => {
                  download(value)
                }}
              ></Button>
            </div>
          </div>
        </Col>
      </Row >
      <Table
        bordered
        rowKey='id'
        style={{ overflowX: 'auto' }}
        columns={columns}
        dataSource={value}
        pagination={{
          total: value.length,
          current: getPageNo('jpstudents'),
          onChange(page) {
            onPageChange('jpstudents', page)
          }
        }}
      />
      <div style={{ marginTop: 24, fontSize: 20 }}>
        จำนวนนักเรียน <span style={{ fontWeight: 'bold' }}>{value.length}</span> คน<br />
        จำนวนนักเรียนทั้งหมด <span style={{ fontWeight: 'bold' }}>{initValue.length}</span> คน
      </div>
      <Modal
        forceRender
        open={qsVisible}
        title={
          <div style={{ padding: '16px 24px', fontWeight: 'bold', fontSize: '18px', lineHeight: '23px', backgroundColor: '#228B90', color: '#F6F6F6', borderRadius: '5px 5px 0px 0px' }}>
            ค้นหา
          </div>
        }
        width='50%'
        closable={false}
        footer={
          <Row justify='center'>
            <Space align='center'>
              <Col>
                <Button
                  type='secondary'
                  size='medium'
                  icon={<DeleteOutlined />}
                  onClick={() => {
                    const empty = {
                      scn: null,
                      firstname: null,
                      lastname: null,
                      companyid: null,
                      coopid: null,
                      status: null
                    }
                    setQs(empty)
                    setSearch('jpstudents', empty)
                    searchForm.setFieldsValue(empty)
                  }}
                >
                  Clear Data
                </Button>
              </Col>
              <Col>
                <Button
                  size='medium'
                  style={{ color: '#FFF', backgroundColor: '#EE7930' }} icon={<CloseOutlined />}
                  onClick={() => {
                    setQsVisible(false)
                  }}
                >
                  ยกเลิก
                </Button>
              </Col>
              <Col>
                <Button
                  type='primary'
                  size='medium'
                  icon={<CheckOutlined />}
                  onClick={() => {
                    const data = searchForm.getFieldsValue()
                    setQs(data)
                    setSearch('jpstudents', data)
                    setQsVisible(false)
                  }}
                >
                  ยืนยัน
                </Button>
              </Col>
            </Space>
          </Row>
        }
      >
        <AntdForm
          layout='vertical'
          initialValues={qs}
          style={{ margin: '8px 32px' }}
          form={searchForm}
          name='searchForm'
        >
          <Row gutter={[24, 24]}>
            <Col span={12}>
              <AntdForm.Item name='scn' label='รหัสนักเรียน' >
                <Input placeholder='รหัสนักเรียน' />
              </AntdForm.Item>
            </Col>
            <Col span={12}>
              <AntdForm.Item name='status' label='สถานะ' >
                <Select placeholder='เลือก' >
                  {((Object.keys(statusObj)).map((o) => (
                    <Select.Option key={o} value={o}>{statusObj[o].name}</Select.Option>
                  )))}
                </Select>
              </AntdForm.Item>
            </Col>
          </Row>
          <Row gutter={[24, 24]}>
            <Col span={12}>
              <AntdForm.Item name='firstname' label='ชื่อ' >
                <Input placeholder='ชื่อ' />
              </AntdForm.Item>
            </Col>
            <Col span={12}>
              <AntdForm.Item name='lastname' label='นามสกุล' >
                <Input placeholder='นามสกุล' />
              </AntdForm.Item>
            </Col>
          </Row>
          <Row gutter={[24, 24]}>
            <Col span={12}>
              <AntdForm.Item name='coopid' label='สหกรณ์' >
                <Select placeholder='เลือก' >
                  {((Object.keys(coopObj)).map((o) => (
                    <Select.Option key={o} value={o}>{coopObj[o].name}</Select.Option>
                  )))}
                </Select>
              </AntdForm.Item>
            </Col>
            <Col span={12}>
              <AntdForm.Item name='companyid' label='บริษัท' >
                <Select placeholder='เลือก' >
                  {((Object.keys(companyObj)).map((o) => (
                    <Select.Option key={o} value={o}>{companyObj[o].name}</Select.Option>
                  )))}
                </Select>
              </AntdForm.Item>
            </Col>
          </Row>
        </AntdForm>
      </Modal>
    </Card >
  )
}

export default List
